<template>
  <li v-show="isMenuItemVisible" class="mt-1">
    <!--
      A section head, not a shouty eyebrow: no uppercase and no letter-spacing, because
      Arabic has no case and tracking disconnects its letterforms.
    -->
    <div v-if="hasSubMenu" class="flex justify-between items-center mt-5 mb-1">
      <span class="px-2 text-xs font-medium text-slate-500 dark:text-slate-300">
        {{ $t(`SIDEBAR.${menuItem.label}`) }}
      </span>
      <div v-if="menuItem.showNewButton" class="flex items-center">
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="secondary"
          icon="add"
          class="p-0 ms-2"
          @click="onClickOpen"
        />
      </div>
    </div>
    <router-link
      v-else
      class="relative flex items-center p-2 m-0 text-sm font-medium leading-4 rounded-lg outline-none transition-colors duration-200 ease-out focus-visible:ring-2 focus-visible:ring-accent-mint/70 focus-visible:ring-offset-1 focus-visible:ring-offset-white dark:focus-visible:ring-offset-ink"
      :class="computedClass"
      :to="menuItem && menuItem.toState"
    >
      <span
        v-if="isActiveView"
        aria-hidden="true"
        class="absolute inset-y-0 w-0.5 h-4 my-auto start-0 rounded-e-full bg-accent-mint"
      />
      <fluent-icon
        :icon="menuItem.icon"
        class="min-w-[1rem] me-1.5"
        size="14"
      />
      {{ $t(`SIDEBAR.${menuItem.label}`) }}
      <span
        v-if="showChildCount(menuItem.count)"
        class="px-1 py-0 mx-1 rounded-md text-xxs font-medium"
        :class="{
          'text-slate-500 dark:text-slate-300': isCountZero && !isActiveView,
          'text-slate-600 dark:text-slate-100': !isCountZero && !isActiveView,
          'bg-woot-50 text-accent-mint dark:bg-mint/[0.14]': isActiveView,
          'bg-slate-50 dark:bg-white/[0.06]': !isActiveView,
        }"
      >
        {{ `${menuItem.count}` }}
      </span>
      <span
        v-if="menuItem.beta"
        data-view-component="true"
        label="Beta"
        class="inline-block px-1 mx-1 font-medium leading-4 border rounded-lg border-accent-sky/40 text-accent-sky text-xxs"
      >
        {{ $t('SIDEBAR.BETA') }}
      </span>
    </router-link>

    <!--
      `mx-0`, not `ms-0`. Foundation emits `ul { margin-left: 1.25rem }` unconditionally
      (`$global-text-direction: ltr` makes `$global-left` = left), so a LOGICAL zero cancels
      the wrong side in Arabic: `ms-0` resolves to `margin-right: 0` and leaves Foundation's
      1.25rem stranded on the left as a stray indent. Zeroing both sides is the only reset
      that holds in both directions.
    -->
    <ul v-if="hasSubMenu" class="mb-0 list-none mx-0">
      <secondary-child-nav-item
        v-for="child in menuItem.children"
        :key="child.id"
        :to="child.toState"
        :label="child.label"
        :label-color="child.color"
        :should-truncate="child.truncateLabel"
        :icon="computedInboxClass(child)"
        :warning-icon="computedInboxErrorClass(child)"
        :show-child-count="showChildCount(child.count)"
        :child-item-count="child.count"
      />
      <router-link
        v-if="showItem(menuItem)"
        v-slot="{ href, navigate }"
        :to="menuItem.toState"
        custom
      >
        <li class="ps-1">
          <a :href="href">
            <woot-button
              size="tiny"
              variant="clear"
              color-scheme="secondary"
              icon="add"
              :data-testid="menuItem.dataTestid"
              @click="e => newLinkClick(e, navigate)"
            >
              {{ $t(`SIDEBAR.${menuItem.newLinkTag}`) }}
            </woot-button>
          </a>
        </li>
      </router-link>
    </ul>
  </li>
</template>

<script>
import { mapGetters } from 'vuex';

import adminMixin from '../../../mixins/isAdmin';
import {
  getInboxClassByType,
  getInboxWarningIconClass,
} from 'dashboard/helper/inbox';

import SecondaryChildNavItem from './SecondaryChildNavItem.vue';
import {
  isOnMentionsView,
  isOnUnattendedView,
} from '../../../store/modules/conversations/helpers/actionHelpers';

// Kept in this `.vue` so Tailwind's content scanner still sees the class names — it reads
// the whole file, script included, but never a plain `.js`.
const ACTIVE_STATE_CLASS = {
  active:
    'bg-woot-25 text-accent-mint hover:bg-woot-50 dark:bg-mint/10 dark:hover:bg-mint/[0.16] active-view',
  idle: 'text-slate-700 hover:bg-slate-50 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-white/[0.05] dark:hover:text-slate-50',
};

export default {
  components: { SecondaryChildNavItem },
  mixins: [adminMixin],
  props: {
    menuItem: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters({
      activeInbox: 'getSelectedInbox',
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      globalConfig: 'globalConfig/get',
    }),
    isCountZero() {
      return this.menuItem.count === 0;
    },
    isActiveView() {
      return this.computedClass.includes('active-view');
    },
    hasSubMenu() {
      return !!this.menuItem.children;
    },
    isMenuItemVisible() {
      if (this.menuItem.globalConfigFlag) {
        return !!this.globalConfig[this.menuItem.globalConfigFlag];
      }
      if (this.menuItem.featureFlag) {
        return this.isFeatureEnabledonAccount(
          this.accountId,
          this.menuItem.featureFlag
        );
      }
      return true;
    },
    isAllConversations() {
      return (
        this.$store.state.route.name === 'inbox_conversation' &&
        this.menuItem.toStateName === 'home'
      );
    },
    isMentions() {
      return (
        isOnMentionsView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_mentions'
      );
    },
    isUnattended() {
      return (
        isOnUnattendedView({ route: this.$route }) &&
        this.menuItem.toStateName === 'conversation_unattended'
      );
    },
    isTeamsSettings() {
      return (
        this.$store.state.route.name === 'settings_teams_edit' &&
        this.menuItem.toStateName === 'settings_teams_list'
      );
    },
    isInboxSettings() {
      return (
        this.$store.state.route.name === 'settings_inbox_show' &&
        this.menuItem.toStateName === 'settings_inbox_list'
      );
    },
    isIntegrationsSettings() {
      return (
        this.$store.state.route.name === 'settings_integrations_webhook' &&
        this.menuItem.toStateName === 'settings_integrations'
      );
    },
    isApplicationsSettings() {
      return (
        this.$store.state.route.name === 'settings_applications_integration' &&
        this.menuItem.toStateName === 'settings_applications'
      );
    },
    isCurrentRoute() {
      return this.$store.state.route.name.includes(this.menuItem.toStateName);
    },

    computedClass() {
      // Active and idle colours are returned as whole, mutually exclusive sets — they can
      // never both land on the element. Tailwind emits `accent-*` BEFORE `slate-*` inside
      // a variant tier, so an active colour listed later in the markup still loses to an
      // idle one that is also present, silently.
      const { active, idle } = ACTIVE_STATE_CLASS;
      // If active inbox is present, do not highlight conversations
      if (this.activeInbox) return idle;
      if (
        this.isAllConversations ||
        this.isMentions ||
        this.isUnattended ||
        this.isCurrentRoute
      ) {
        return active;
      }
      if (this.hasSubMenu) {
        if (
          this.isTeamsSettings ||
          this.isInboxSettings ||
          this.isIntegrationsSettings ||
          this.isApplicationsSettings
        ) {
          return active;
        }
        return idle;
      }

      return idle;
    },
  },
  methods: {
    computedInboxClass(child) {
      const { type, phoneNumber } = child;
      if (!type) return '';
      const classByType = getInboxClassByType(type, phoneNumber);
      return classByType;
    },
    computedInboxErrorClass(child) {
      const { type, reauthorizationRequired } = child;
      if (!type) return '';
      const warningClass = getInboxWarningIconClass(
        type,
        reauthorizationRequired
      );
      return warningClass;
    },
    newLinkClick(e, navigate) {
      if (this.menuItem.newLinkRouteName) {
        navigate(e);
      } else if (this.menuItem.showModalForNewItem) {
        if (this.menuItem.modalName === 'AddLabel') {
          e.preventDefault();
          this.$emit('add-label');
        }
      }
    },
    showItem(item) {
      return this.isAdmin && !!item.newLink;
    },
    onClickOpen() {
      this.$emit('open');
    },
    showChildCount(count) {
      return Number.isInteger(count);
    },
  },
};
</script>
